import React, { FC, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Alert } from 'reactstrap';
import {
  CashierError,
  CashierInfo,
  CashierNote,
  CashierWarning,
  LicenseWarnings,
} from '../../../shared/user/cashier';
import { PermissionBoundary } from '../../components';
import { TranslationKey, useTranslation } from '../../translations';

interface CashierMessageProps {
  readonly title: TranslationKey;

  readonly licenseWarnings?: LicenseWarnings[];
  readonly openUpdateFromScannerModal?: () => void;
}

interface InfoCashierMessageProps extends CashierMessageProps {
  readonly type: 'info';
  readonly messages: CashierNote<CashierInfo>[];
}

interface ErrorCashierMessageProps extends CashierMessageProps {
  readonly type: 'error';
  readonly messages: CashierNote<CashierError>[];
}

interface WarningCashierMessageProps extends CashierMessageProps {
  readonly type: 'warning';
  readonly messages: CashierNote<CashierWarning>[];
}

const cashierMessages = {
  info: {
    [CashierInfo.ReturningAttendee]: 'common.returning_attendee',
    [CashierInfo.VouchersApplied]: 'cashier.vouchers_applied',
    [CashierInfo.NoBarcodeScanned]: 'cashier.no_barcode_scanned',
    [CashierInfo.UserIsDealer]: 'cashier.user_is_dealer',
  } satisfies Record<CashierInfo, TranslationKey>,
  error: {
    [CashierError.UnderReview]: 'cashier.under_review',
    [CashierError.Banned]: 'cashier.banned',
    [CashierError.ItemsPendingPayment]: 'cashier.items_pending_payment',
    [CashierError.NotRegistered]: 'cashier.not_registered',
    [CashierError.NoPaidRegistration]: 'cashier.no_paid_registration',
    [CashierError.ProductTypeAgeViolation]: 'cashier.product_type_age_violation',
    [CashierError.NoBarcodeScanned]: 'cashier.no_barcode_scanned_error',
    [CashierError.OutstandingBalance]: 'cashier.outstanding_balance',
  } satisfies Record<CashierError, TranslationKey>,
  warning: {
    [CashierWarning.Minor]: 'cashier.minor',
    [CashierWarning.Volunteer]: 'cashier.volunteer',
    [CashierWarning.BannedWordUsage]: 'cashier.banned_word_usage',
    [CashierWarning.Guardian]: 'cashier.guardian',
    [CashierWarning.OutstandingDealerPayment]: 'cashier.outstanding_dealer_payment',
  } satisfies Record<CashierWarning, TranslationKey>,
};

export const CashierMessages: FC<
  InfoCashierMessageProps | WarningCashierMessageProps | ErrorCashierMessageProps
> = ({
  licenseWarnings,
  type,
  title,
  messages,
  openUpdateFromScannerModal,
}): React.JSX.Element | null => {
  const { ts, tsmd } = useTranslation();
  const openScannerModal = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      openUpdateFromScannerModal?.();
    },
    [openUpdateFromScannerModal],
  );

  if (messages.length === 0 && (licenseWarnings?.length ?? 0) === 0) {
    return null;
  }

  const hasDobMismatch = licenseWarnings?.includes('dobMismatch');
  const hasNameMismatch =
    licenseWarnings?.includes('firstNameMismatch') || licenseWarnings?.includes('lastNameMismatch');

  const lookupTable = cashierMessages[type];

  return (
    <div className="cashier-status-message">
      <p className={`text-${type === 'error' ? 'danger' : type}`} style={{ marginBottom: 0 }}>
        {ts(title)}
      </p>
      <ul>
        {messages.map((message) => (
          // TODO: This is hacky
          <li key={message.code}>
            {tsmd(
              lookupTable[message.code as CashierInfo],
              ...((message.args as string[] | undefined) ?? []),
            )}
          </li>
        ))}
        {hasDobMismatch && <li>{ts('date_of_birth_does_not')}</li>}
        {hasNameMismatch && (
          <li>
            {ts('name_does_not_match_scanned')}
            <PermissionBoundary inline requiredPermissions="user:update">
              &#40;
              <Link id="openUpdateInfoFromScan" onClick={openScannerModal} to="">
                {ts('common.update')}
              </Link>
              &#41;
            </PermissionBoundary>
          </li>
        )}
        {licenseWarnings?.includes('expired') && <li>{ts('scanned_id_has_an_expiration')}</li>}
      </ul>
    </div>
  );
};

interface NotesListProps {
  readonly notes: string[];
}

export const NotesList: FC<NotesListProps> = ({ notes }) => {
  const { ts } = useTranslation();

  if (notes.length === 0) {
    return null;
  }

  return (
    <div className="cashier-status-message cashier-status-message-notes">
      <p className="text-notes">{ts('user_notes')}</p>
      {notes.map((note) => (
        <Alert color="warning" key={note}>
          {note}
        </Alert>
      ))}
    </div>
  );
};
